import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Phone, Mail, MapPin, ShieldCheck, Award, ThumbsUp, ArrowRight } from 'lucide-react'
import LightboxGallery from '../components/LightboxGallery'

export default function ServiceDetail({ serviceSlug }) {
  const { slug: paramSlug } = useParams()
  const slug = serviceSlug || paramSlug
  const [pageData, setPageData] = useState(null)
  const [galleryData, setGalleryData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        // Fetch pages
        const pagesRes = await fetch('https://api.dev.auxo.dk/public/content/pages')
        if (pagesRes.ok) {
          const pages = await pagesRes.json()
          const page = pages.find((p) => p.page_slug === slug)
          setPageData(page)
        }

        // Fetch galleries
        const galleriesRes = await fetch('https://api.dev.auxo.dk/public/content/galleries')
        if (galleriesRes.ok) {
          const galleries = await galleriesRes.json()
          const gallery = galleries.find((g) => g.gallery_slug === slug)
          setGalleryData(gallery)
        }
      } catch (err) {
        console.error('Error fetching service detail data:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [slug])

  const rawTitle = pageData?.title?.da || pageData?.title || ''
  const title = rawTitle
    .replace(/\s*i\s+Randers\s+(?:og|&)\s+Hobro\s*/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  const metaDesc = pageData?.meta_description?.da || pageData?.meta_description
  const rawContentHtml = pageData?.content?.da || pageData?.content

  // Strip all <a> tags while preserving their inner text
  const contentHtml = rawContentHtml
    ? rawContentHtml.replace(/<a\b[^>]*>([\s\S]*?)<\/a>/gi, '$1')
    : ''

  // Extract first h1 and first p from contentHtml
  let extractedHeading = ''
  let extractedParagraph = ''
  let remainingContentHtml = contentHtml

  if (contentHtml) {
    // Match first <h1>...</h1>
    const h1Match = contentHtml.match(/<h1[^>]*>([\s\S]*?)<\/h1>/i)
    // Match first <p>...</p>
    const pMatch = contentHtml.match(/<p[^>]*>([\s\S]*?)<\/p>/i)

    if (h1Match) {
      extractedHeading = h1Match[1]
      remainingContentHtml = remainingContentHtml.replace(h1Match[0], '')
    }
    if (pMatch) {
      extractedParagraph = pMatch[1]
      remainingContentHtml = remainingContentHtml.replace(pMatch[0], '')
    }
  }

  // Remove "Få et uforpligtende tilbud" heading and the paragraph immediately following it
  if (remainingContentHtml) {
    // Remove h2 or span with "Få et uforpligtende tilbud" and the following paragraph
    remainingContentHtml = remainingContentHtml.replace(/<(?:h2|span)[^>]*>\s*Få et uforpligtende tilbud[\s\S]*?<\/(?:h2|span)>\s*<p[^>]*>[\s\S]*?<\/p>/gi, '')
    // Also remove any standalone paragraph starting with "Går du med planer om" or containing the contact info
    remainingContentHtml = remainingContentHtml.replace(/<p[^>]*>\s*Går du med planer om[\s\S]*?<\/p>/gi, '')
    // Also catch any standalone span/heading with "Få et uforpligtende tilbud"
    remainingContentHtml = remainingContentHtml.replace(/<(?:h2|span)[^>]*>\s*Få et uforpligtende tilbud[\s\S]*?<\/(?:h2|span)>/gi, '')
  }

  // Clean up heading to remove location suffixes
  const displayHeading = extractedHeading
    ? extractedHeading
        .replace(/\s*i\s+Randers,\s+Hobro\s+og\s+omegn\s*/gi, '')
        .replace(/\s*i\s+Randers\s+(?:og|&)\s+Hobro\s*/gi, '')
        .replace(/\s+/g, ' ')
        .trim()
    : title

  const displayParagraph = extractedParagraph

  const bannerImage = slug === 'facademaling'
    ? 'https://assets.auxodev.dk/assets/bbcf0f14-2bcd-4cd4-98b4-2d62ce00d60f/image00020_1.jpeg'
    : slug === 'hoejtryksrensning'
    ? 'https://assets.auxodev.dk/assets/bbcf0f14-2bcd-4cd4-98b4-2d62ce00d60f/image00013_1.jpeg'
    : (pageData?.banner_image?.[0] || 'https://assets.auxodev.dk/assets/bbcf0f14-2bcd-4cd4-98b4-2d62ce00d60f/1fec9621-01fc-4c1d-b2fb-740aa2ee5a1a.jpeg')

  // Update SEO Title and Meta Description
  useEffect(() => {
    if (title) {
      document.title = `${title} | Malerfirma Villy Poulsen ApS`
    }
    if (metaDesc) {
      const metaDescriptionTag = document.querySelector('meta[name="description"]')
      if (metaDescriptionTag) {
        metaDescriptionTag.setAttribute('content', metaDesc)
      }
    }
  }, [title, metaDesc])

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#01529E]" />
      </div>
    )
  }

  if (!pageData) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center">
        <h1 className="text-3xl font-black text-gray-900">Siden blev ikke fundet</h1>
        <p className="text-gray-500 mt-2">Vi kunne desværre ikke finde den ønskede ydelsesside.</p>
        <Link
          to="/"
          className="mt-6 inline-block px-6 py-3 rounded-xl bg-[#01529E] text-white font-bold text-sm hover:bg-[#01529E]/90 transition-colors"
        >
          Gå til forsiden
        </Link>
      </div>
    )
  }

  return (
    <div className="bg-white">
      {/* Banner Header */}
      <section className="relative bg-[#20252C] text-white py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={bannerImage}
            alt={title}
            className="w-full h-full object-cover object-center opacity-45"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-950/80 via-gray-900/50 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#01529E]/90 text-white text-xs font-bold uppercase tracking-widest mb-6">
              Vores Ydelser
            </span>
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-tight"
              dangerouslySetInnerHTML={{ __html: displayHeading }}
            />
            {displayParagraph && (
              <p 
                className="mt-6 text-lg text-gray-300 leading-relaxed max-w-2xl"
                dangerouslySetInnerHTML={{ __html: displayParagraph }}
              />
            )}
          </div>
        </div>
      </section>

      {/* Main Content & Sidebar */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left Column: Rich Text Content & Gallery */}
            <div className="lg:col-span-8 space-y-12">
              {/* Verbatim Content */}
              <div
                dangerouslySetInnerHTML={{ __html: remainingContentHtml }}
                className="max-w-none text-gray-700 leading-relaxed
                  [&_h1]:text-3xl [&_h1]:font-black [&_h1]:text-gray-900 [&_h1]:mt-8 [&_h1]:mb-4
                  [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-gray-900 [&_h2]:mt-8 [&_h2]:mb-4 [&_h2]:border-b [&_h2]:border-gray-100 [&_h2]:pb-2
                  [&_h3]:text-xl [&_h3]:font-bold [&_h3]:text-gray-900 [&_h3]:mt-6 [&_h3]:mb-3
                  [&_p]:text-gray-600 [&_p]:leading-relaxed [&_p]:mb-5
                  [&_ul]:grid [&_ul]:grid-cols-1 [&_ul]:sm:grid-cols-2 [&_ul]:gap-3 [&_ul]:my-8 [&_ul]:list-none [&_ul]:pl-0
                  [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-6 [&_ol]:space-y-2 [&_ol]:text-gray-600
                  [&_li]:relative [&_li]:pl-12 [&_li]:pr-4 [&_li]:py-3.5 [&_li]:bg-gray-50 [&_li]:border [&_li]:border-gray-100 [&_li]:rounded-xl [&_li]:text-gray-800 [&_li]:font-semibold [&_li]:shadow-xs [&_li]:before:content-['✓'] [&_li]:before:absolute [&_li]:before:left-4 [&_li]:before:top-[15px] [&_li]:before:text-white [&_li]:before:bg-[#ED1C26] [&_li]:before:w-6 [&_li]:before:h-6 [&_li]:before:rounded-full [&_li]:before:flex [&_li]:before:items-center [&_li]:before:justify-center [&_li]:before:text-xs [&_li]:before:font-bold
                  [&_strong]:font-bold [&_strong]:text-gray-900"
              />

              {/* Connected Gallery */}
              {galleryData && (
                <div className="border-t border-gray-100 pt-12">
                  <LightboxGallery
                    images={galleryData.images}
                    title={galleryData.title?.da || galleryData.title}
                    description={galleryData.description?.da || galleryData.description}
                  />
                </div>
              )}

              {/* Custom section for facademaling */}
              {slug === 'facademaling' && !galleryData && (
                <div className="border-t border-gray-100 pt-12">
                  <LightboxGallery
                    images={[
                      "https://assets.auxodev.dk/assets/bbcf0f14-2bcd-4cd4-98b4-2d62ce00d60f/6e18bc07-f2d8-4f91-9959-8b454c71f5c2.jpg",
                      "https://assets.auxodev.dk/assets/bbcf0f14-2bcd-4cd4-98b4-2d62ce00d60f/3f06638e-0645-46b1-9e75-1d9023701772.jpg",
                      "https://assets.auxodev.dk/assets/bbcf0f14-2bcd-4cd4-98b4-2d62ce00d60f/image00018_5.jpeg",
                      "https://assets.auxodev.dk/assets/bbcf0f14-2bcd-4cd4-98b4-2d62ce00d60f/image00019_5.jpeg",
                      "https://assets.auxodev.dk/assets/bbcf0f14-2bcd-4cd4-98b4-2d62ce00d60f/image00020_6.jpeg",
                      "https://assets.auxodev.dk/assets/bbcf0f14-2bcd-4cd4-98b4-2d62ce00d60f/image00021_4.jpeg"
                    ]}
                    title="Facademaling"
                    description="Professionel facademaling udført af Malerfirma Villy Poulsen ApS."
                  />
                </div>
              )}
            </div>

            {/* Right Column: Sidebar */}
            <div className="lg:col-span-4 space-y-8">
              {/* Trust Box */}
              <div className="bg-gray-50 rounded-2xl p-6 sm:p-8 border border-gray-100">
                <h3 className="text-lg font-bold text-gray-900 mb-6">Hvorfor vælge os?</h3>
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="p-2.5 rounded-xl bg-[#01529E]/5 text-[#01529E] shrink-0 h-11 w-11 flex items-center justify-center">
                      <Award className="h-5 w-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-sm">Over 30 års erfaring</h4>
                      <p className="text-gray-500 text-xs mt-1 leading-relaxed">
                        Vi har løst utallige maleropgaver og har den nødvendige ekspertise til ethvert projekt.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="p-2.5 rounded-xl bg-[#01529E]/5 text-[#01529E] shrink-0 h-11 w-11 flex items-center justify-center">
                      <ShieldCheck className="h-5 w-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-sm">Danske Malermestre</h4>
                      <p className="text-gray-500 text-xs mt-1 leading-relaxed">
                        Vores medlemskab sikrer dig professionelt udført malerarbejde og fuld garanti.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="p-2.5 rounded-xl bg-[#01529E]/5 text-[#01529E] shrink-0 h-11 w-11 flex items-center justify-center">
                      <ThumbsUp className="h-5 w-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-sm">Kvalitet & Stabilitet</h4>
                      <p className="text-gray-500 text-xs mt-1 leading-relaxed">
                        Vi leverer altid til aftalt tid og pris, og vi forlader aldrig en opgave før du er 100% tilfreds.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Contact Box */}
              <div className="bg-[#20252C] text-white rounded-2xl p-6 sm:p-8 relative overflow-hidden">
                <div className="absolute right-0 bottom-0 translate-x-10 translate-y-10 w-40 h-40 bg-[#01529E]/10 rounded-full blur-2xl" />
                <h3 className="text-lg font-bold mb-4">Få et uforpligtende tilbud</h3>
                <p className="text-gray-400 text-xs leading-relaxed mb-6">
                  Vi besvarer gerne dine spørgsmål og giver et godt tilbud på dit malerarbejde. Ring eller send en mail.
                </p>
                <div className="space-y-4 text-sm">
                  <a href="tel:40637670" className="flex items-center gap-3 hover:text-[#FDF300] transition-colors">
                    <Phone className="h-5 w-5 text-[#ED1C26]" />
                    <span>Mobil: 40 63 76 70</span>
                  </a>
                  <a href="mailto:mail@malerfirma-villy-poulsen.dk" className="flex items-center gap-3 hover:text-[#FDF300] transition-colors whitespace-nowrap">
                    <Mail className="h-5 w-5 text-[#ED1C26]" />
                    <span>mail@malerfirma-villy-poulsen.dk</span>
                  </a>
                </div>
                <Link
                  to="/kontakt"
                  className="mt-6 w-full py-3 rounded-xl bg-[#01529E] text-white font-bold text-xs uppercase tracking-wider text-center block hover:bg-[#01529E]/90 transition-colors cursor-pointer"
                >
                  Kontakt os nu
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
